import React from 'react';
import {
    Zap, AlertTriangle, FileCheck, Beaker, BarChart2, GitBranch, LifeBuoy
} from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

export const UserGuideAppCards: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    return (
        <section id="core-modules">
            <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                <Zap className="text-blue-500" />
                App Cards & Features
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* FRACAS Card */}
                <div id="card-fracas" className={clsx('p-5 rounded-xl border card-hover', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                            <AlertTriangle size={24} />
                        </div>
                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>FRACAS</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 h-10">Centralized incident management with 8D escalation workflow.</p>
                    <button onClick={() => navigate('/fracas')} className="w-full py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors">
                        Open Module
                    </button>
                </div>

                {/* 8D Card */}
                <div id="card-8d" className={clsx('p-5 rounded-xl border card-hover', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <FileCheck size={24} />
                        </div>
                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>8D Reports</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 h-10">Structured team-based problem solving for complex issues.</p>
                    <button onClick={() => navigate('/8d')} className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                        Open Module
                    </button>
                </div>

                {/* Lab Card */}
                <div id="card-lab" className={clsx('p-5 rounded-xl border card-hover', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            <Beaker size={24} />
                        </div>
                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>Lab Manager</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 h-10">Track samples, tests, and integrate results with incidents.</p>
                    <button onClick={() => navigate('/lab')} className="w-full py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors">
                        Open Module
                    </button>
                </div>

                {/* Analytics Card */}
                <div id="card-analytics" className={clsx('p-5 rounded-xl border card-hover', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            <BarChart2 size={24} />
                        </div>
                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>Analytics</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 h-10">MTBF, Weibull, and reliability growth tracking tools.</p>
                    <button onClick={() => navigate('/analytics')} className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                        Open Module
                    </button>
                </div>

                {/* RCA Card */}
                <div id="card-rca" className={clsx('p-5 rounded-xl border card-hover', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <GitBranch size={24} />
                        </div>
                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>RCA Tools</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 h-10">5-Why, Ishikawa, and Fault Tree Analysis editors.</p>
                    <button onClick={() => navigate('/rca')} className="w-full py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors">
                        Open Module
                    </button>
                </div>

                {/* Support Card */}
                <div id="card-support" className={clsx('p-5 rounded-xl border card-hover', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                            <LifeBuoy size={24} />
                        </div>
                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>Support</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 h-10">Contact our engineering support team for assistance.</p>
                    <button className="w-full py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors">
                        Contact Us
                    </button>
                </div>
            </div>
        </section>
    );
};
